/**
 * useChatThreads — `GET /api/v1/chat/threads?chartId=` (past conversations about one chart);
 * useDeleteChatThread — `DELETE /api/v1/chat/threads/:id`.
 *
 * chartId: a chart id, or '' for «Я» (the server treats an empty value as the own chart).
 * `undefined` disables the query.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { ApiError, ensureOk } from '../lib/api-error';
import { useAuth } from '../store/auth';

export interface ThreadItem {
  id: string;
  chartId: string | null;
  title: string | null;
  lastMessageAt: string;
  surface: string;
  coursePersona?: import('../types/course').CourseChatPersona | null;
}

/** All conversations of the caller, newest first, about every chart («Я» and saved ones). No `chartId` → the server returns them all. */
export async function fetchAllChatThreads(jwt: string): Promise<ThreadItem[]> {
  const res = await ensureOk(await apiFetch('/api/v1/chat/threads', jwt));
  return ((await res.json()) as { threads: ThreadItem[] }).threads;
}

export function useAllChatThreads(): {
  threads: ThreadItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);
  const q = useQuery<ThreadItem[], Error>({
    queryKey: ['chat-threads', 'all'],
    enabled: !!jwt,
    queryFn: () => fetchAllChatThreads(jwt as string),
  });
  return { threads: q.data ?? [], isLoading: q.isLoading, isError: q.isError, refetch: () => void q.refetch() };
}

export function useChatThreads(chartId: string | undefined): {
  threads: ThreadItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);
  const q = useQuery<ThreadItem[], Error>({
    queryKey: ['chat-threads', chartId],
    enabled: !!jwt && chartId !== undefined,
    // A 404 means the chart is gone; retrying cannot help (same rule as useChartDetail).
    retry: (failureCount, error) => !(error instanceof ApiError && error.status === 404) && failureCount < 3,
    queryFn: async () => {
      const res = await ensureOk(
        await apiFetch(`/api/v1/chat/threads?chartId=${encodeURIComponent(chartId ?? '')}`, jwt as string),
      );
      return ((await res.json()) as { threads: ThreadItem[] }).threads;
    },
  });
  return { threads: q.data ?? [], isLoading: q.isLoading, isError: q.isError, refetch: () => void q.refetch() };
}

/**
 * Deletes one conversation (the stars it cost are not returned). A 404 means it is already gone —
 * counts as deleted. On success the thread lists and the chart details (`conversationsCount`) are refreshed.
 */
export function useDeleteChatThread(): {
  mutateAsync: (id: string) => Promise<void>;
  isPending: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const m = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      if (!jwt) throw new Error('not_authenticated');
      try {
        await ensureOk(await apiFetch(`/api/v1/chat/threads/${encodeURIComponent(id)}`, jwt, { method: 'DELETE' }));
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 404)) throw err;
      }
    },
    onSuccess: (_out, id) => {
      qc.removeQueries({ queryKey: ['chat-thread', id], exact: true });
      qc.removeQueries({ queryKey: ['course-chat-thread', id], exact: true });
      void qc.invalidateQueries({ queryKey: ['course-chat-preview'] });
      void qc.invalidateQueries({ queryKey: ['chat-threads'] });
      void qc.invalidateQueries({ queryKey: ['chart'] });
      void qc.invalidateQueries({ queryKey: ['charts'] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending };
}
